import React, { useState, useEffect, useRef } from 'react';
import { format, parseISO } from 'date-fns';
import TimeOffConflictDialog from './TimeOffConflictDialog';
import TimeOffDeleteDialog, { DeleteOutcome } from './TimeOffDeleteDialog';
import TimeOffRestoreDialog from './TimeOffRestoreDialog';
import {
  AdminStaff,
  Appointment,
  TimeOff,
  fetchAdminStaff,
  fetchAdminTimeOff,
  createAdminTimeOff,
  updateAdminTimeOff,
  ApiError,
} from '../api';
import { salonNow } from '../lib/datetime';
import {
  Plus,
  Trash2,
  Check,
  Pencil,
  Loader2,
  CalendarOff,
  Store,
  User,
  AlertCircle,
} from 'lucide-react';
import { TIME_OPTIONS, formatTime12 } from '../lib/time';

/** "Mon 10 Aug 2026" — dates are plain yyyy-MM-dd, so parse them as-is (no timezone shift). */
function formatDay(isoDate: string): string {
  return format(parseISO(isoDate), 'EEE d MMM yyyy');
}

/** The one-line human summary of a block, e.g. "10–20 Aug 2026 · All day". */
function describe(entry: TimeOff): string {
  const days =
    entry.startDate === entry.endDate
      ? formatDay(entry.startDate)
      : `${formatDay(entry.startDate)} → ${formatDay(entry.endDate)}`;
  if (!entry.startTime && !entry.endTime) return `${days} · All day`;
  const from = entry.startTime ? formatTime12(entry.startTime) : 'start of day';
  const to = entry.endTime ? formatTime12(entry.endTime) : 'end of day';
  return `${days} · ${from} → ${to}`;
}

const today = () => format(salonNow(), 'yyyy-MM-dd');

interface TimeOffForm {
  /** 'salon' closes the whole shop; a number closes one worker. */
  who: 'salon' | number;
  startDate: string;
  endDate: string;
  allDay: boolean;
  startTime: string;
  endTime: string;
  note: string;
}

const emptyForm = (): TimeOffForm => ({
  who: 'salon',
  startDate: today(),
  endDate: today(),
  allDay: true,
  startTime: '09:00',
  endTime: '18:00',
  note: '',
});

interface AdminTimeOffProps {
  /** Called after bookings are cancelled, so the calendar tab isn't left stale. */
  onAppointmentsChanged?: () => void;
  /**
   * The refreshed list, after a block is added, edited or lifted.
   *
   * Handed over rather than announced, so the calendar's banner reuses the fetch
   * this tab just made instead of asking for the same rows again. Not called at
   * all when that fetch failed — the parent keeps what it has.
   */
  onTimeOffChanged?: (rows: TimeOff[]) => void;
}

export default function AdminTimeOff({
  onAppointmentsChanged,
  onTimeOffChanged,
}: AdminTimeOffProps) {
  const [entries, setEntries] = useState<TimeOff[]>([]);
  const [workers, setWorkers] = useState<AdminStaff[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<TimeOffForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [savedMessage, setSavedMessage] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<TimeOff | null>(null);
  const [loadError, setLoadError] = useState(false);
  // Bookings the just-saved block landed on top of. The block is saved either
  // way; only the bookings are still undecided.
  const [conflicts, setConflicts] = useState<Appointment[]>([]);
  const [conflictRange, setConflictRange] = useState('');
  const [conflictBlockId, setConflictBlockId] = useState<number | null>(null);
  // The mirror image: bookings a just-lifted block had cancelled, offered back.
  const [restorable, setRestorable] = useState<Appointment[]>([]);

  // Cleared on unmount so a pending "Saved" flash can't fire into a gone
  // component, and re-armed on each save so a quick second save doesn't have the
  // first save's timer blank the banner early.
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    load();
    return () => {
      if (savedTimer.current) clearTimeout(savedTimer.current);
    };
  }, []);

  const load = async (): Promise<TimeOff[] | null> => {
    setLoading(true);
    let loaded: TimeOff[] | null = null;
    try {
      // Fetched here rather than passed down from the dashboard: that list comes
      // from the public tenant detail, which only carries *active* workers, so an
      // inactive worker's time off would render with a blank name.
      const [t, w] = await Promise.all([fetchAdminTimeOff(), fetchAdminStaff()]);
      setEntries(t);
      setWorkers(w);
      setLoadError(false);
      loaded = t;
    } catch {
      // A failed load must NOT fall through to the empty state: "No time off
      // scheduled" is a claim about the salon's availability, and an owner who
      // reads it after a failed fetch will book over their own holiday or add the
      // block twice. Keep whatever loaded last on screen and say the load failed.
      setLoadError(true);
    }
    setLoading(false);
    return loaded;
  };

  const flashSaved = () => {
    setSavedMessage('Saved');
    if (savedTimer.current) clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSavedMessage(''), 2500);
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (entry: TimeOff) => {
    setEditingId(entry.id);
    setForm({
      who: entry.staffId ?? 'salon',
      startDate: entry.startDate,
      endDate: entry.endDate,
      allDay: !entry.startTime && !entry.endTime,
      startTime: entry.startTime ?? '09:00',
      endTime: entry.endTime ?? '18:00',
      note: entry.note,
    });
    setFormError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const set = <K extends keyof TimeOffForm>(key: K, value: TimeOffForm[K]) =>
    setForm((p) => ({ ...p, [key]: value }));

  // yyyy-MM-dd sorts correctly as a plain string, so no parsing needed here.
  const datesInvalid = !form.startDate || !form.endDate || form.endDate < form.startDate;
  const timesInvalid =
    !form.allDay && form.startDate === form.endDate && form.endTime <= form.startTime;
  const canSave = !datesInvalid && !timesInvalid;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setFormError('');
    const payload = {
      staffId: form.who === 'salon' ? null : form.who,
      startDate: form.startDate,
      endDate: form.endDate,
      startTime: form.allDay ? null : form.startTime,
      endTime: form.allDay ? null : form.endTime,
      note: form.note,
    };
    try {
      const saved =
        editingId === null
          ? await createAdminTimeOff(payload)
          : await updateAdminTimeOff(editingId, payload);
      const rows = await load();
      if (rows) onTimeOffChanged?.(rows);
      flashSaved();
      closeForm();
      // Moving a block can do both at once — later start, later end — stranding
      // bookings at the new end while freeing ones at the old start. Queue both;
      // the restore dialog opens as the conflict one closes, so the owner deals
      // with the people they've just cut off before the ones they've freed.
      if (saved.restorable && saved.restorable.length > 0) setRestorable(saved.restorable);
      if (saved.conflicts && saved.conflicts.length > 0) {
        setConflictRange(
          saved.startDate === saved.endDate
            ? formatDay(saved.startDate)
            : `${formatDay(saved.startDate)} → ${formatDay(saved.endDate)}`,
        );
        setConflictBlockId(saved.id);
        setConflicts(saved.conflicts);
      }
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Could not save this time off.');
    }
    setSaving(false);
  };

  // Deleting asks about the bookings it would bring back before it deletes
  // anything, so both halves resolve inside the dialog — see TimeOffDeleteDialog.
  const handleDeleteClosed = async ({ deleted, restoredAny }: DeleteOutcome) => {
    setDeleteTarget(null);
    if (!deleted) return;
    const rows = await load();
    if (rows) onTimeOffChanged?.(rows);
    if (restoredAny) onAppointmentsChanged?.();
  };

  return (
    <div className="space-y-4">
      <div className="border-default bg-surface overflow-hidden rounded-xl border shadow-sm">
        <div className="border-subtle flex items-center justify-between gap-3 border-b px-4 py-4 sm:px-6 sm:py-5">
          <div className="min-w-0">
            <h2 className="text-fg text-base font-bold sm:text-lg">Time Off</h2>
            <p className="text-fg-muted mt-0.5 hidden text-xs sm:block sm:text-sm">
              Close the salon or a single worker for a holiday, a half day, or a couple of hours.
              Customers can't book inside these.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            {savedMessage && (
              <span className="text-success flex items-center gap-1 text-xs font-medium sm:text-sm">
                <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                {savedMessage}
              </span>
            )}
            {!showForm && (
              <button
                onClick={openAdd}
                className="bg-brand-600 hover:bg-brand-700 flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium text-white transition-colors sm:gap-2 sm:px-4 sm:text-sm"
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Add Time Off</span>
                <span className="sm:hidden">Add</span>
              </button>
            )}
          </div>
        </div>

        {/* ── Add / edit form ── */}
        {showForm && (
          <form
            onSubmit={handleSubmit}
            className="border-subtle bg-canvas/40 space-y-5 border-b px-4 py-5 sm:px-6"
          >
            <div>
              <label className="text-fg-secondary mb-1 block text-xs font-semibold tracking-wide uppercase">
                Who's away
              </label>
              <select
                value={form.who}
                onChange={(e) =>
                  set('who', e.target.value === 'salon' ? 'salon' : Number(e.target.value))
                }
                className="focus:ring-brand-600 border-default w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2 sm:w-72"
              >
                <option value="salon">The whole salon</option>
                {workers.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                    {w.isActive ? '' : ' (inactive)'}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-fg-secondary mb-1 block text-xs font-semibold tracking-wide uppercase">
                  First day away
                </label>
                <input
                  type="date"
                  required
                  value={form.startDate}
                  onChange={(e) => {
                    const startDate = e.target.value;
                    // Keep the range valid while typing instead of showing an error.
                    set('startDate', startDate);
                    if (form.endDate < startDate) set('endDate', startDate);
                  }}
                  className="focus:ring-brand-600 border-default w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2"
                />
              </div>
              <div>
                <label className="text-fg-secondary mb-1 block text-xs font-semibold tracking-wide uppercase">
                  Last day away
                </label>
                <input
                  type="date"
                  required
                  min={form.startDate}
                  value={form.endDate}
                  onChange={(e) => set('endDate', e.target.value)}
                  className="focus:ring-brand-600 border-default w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2"
                />
                {datesInvalid && (
                  <p className="text-danger mt-1 text-xs">
                    The last day can't be before the first day.
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="text-fg-secondary flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.allDay}
                  onChange={(e) => set('allDay', e.target.checked)}
                  className="text-accent focus:ring-brand-600 border-strong h-4 w-4 rounded"
                />
                Away all day
              </label>

              {!form.allDay && (
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="text-fg-secondary mb-1 block text-xs font-semibold tracking-wide uppercase">
                      Leaving at
                    </label>
                    <select
                      value={form.startTime}
                      onChange={(e) => set('startTime', e.target.value)}
                      className="focus:ring-brand-600 border-default w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2"
                    >
                      {TIME_OPTIONS.map((t) => (
                        <option key={t} value={t}>
                          {formatTime12(t)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-fg-secondary mb-1 block text-xs font-semibold tracking-wide uppercase">
                      Back at
                    </label>
                    <select
                      value={form.endTime}
                      onChange={(e) => set('endTime', e.target.value)}
                      className="focus:ring-brand-600 border-default w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2"
                    >
                      {TIME_OPTIONS.map((t) => (
                        <option key={t} value={t}>
                          {formatTime12(t)}
                        </option>
                      ))}
                    </select>
                    {timesInvalid && (
                      <p className="text-danger mt-1 text-xs">
                        On a single day, "back at" must be after "leaving at".
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* A multi-day block with times is ONE continuous span, not a daily
                  repeat. Spelling it out is the only thing that makes that visible. */}
              {!form.allDay && form.endDate > form.startDate && !timesInvalid && (
                <p className="bg-accent-soft text-accent border-accent/30 mt-3 rounded-lg border px-3 py-2 text-xs">
                  Away from <strong>{formatDay(form.startDate)}</strong> at{' '}
                  <strong>{formatTime12(form.startTime)}</strong> until{' '}
                  <strong>{formatDay(form.endDate)}</strong> at{' '}
                  <strong>{formatTime12(form.endTime)}</strong> — the days in between are fully off.
                </p>
              )}
            </div>

            <div>
              <label className="text-fg-secondary mb-1 block text-xs font-semibold tracking-wide uppercase">
                Private note
              </label>
              <input
                type="text"
                maxLength={200}
                value={form.note}
                onChange={(e) => set('note', e.target.value)}
                placeholder="e.g. Summer holiday"
                className="focus:ring-brand-600 border-default w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2"
              />
              <p className="text-fg-muted mt-1 text-xs">
                Only visible to you. Customers never see this.
              </p>
            </div>

            {formError && (
              <div className="border-danger/25 bg-danger-soft text-danger rounded-lg border px-3 py-2 text-sm">
                {formError}
              </div>
            )}

            <div className="flex gap-2">
              <button
                type="submit"
                disabled={saving || !canSave}
                className="bg-brand-600 hover:bg-brand-700 flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors disabled:opacity-50"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {editingId === null ? 'Add Time Off' : 'Save Changes'}
              </button>
              <button
                type="button"
                onClick={closeForm}
                className="border-default text-fg-secondary hover:bg-canvas rounded-lg border px-4 py-2 text-sm font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Refresh failed but we still have prior results on screen */}
        {loadError && entries.length > 0 && (
          <div className="border-danger/20 bg-danger-soft text-danger flex items-center justify-between gap-3 border-b px-4 py-2.5 text-sm sm:px-6">
            <span className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 shrink-0" />
              Couldn't refresh — showing the last loaded list.
            </span>
            <button
              onClick={load}
              className="text-danger shrink-0 font-semibold underline-offset-2 hover:underline"
            >
              Retry
            </button>
          </div>
        )}

        {/* ── List ── */}
        {loading ? (
          <div className="flex justify-center px-4 py-10">
            <Loader2 className="text-fg-subtle h-5 w-5 animate-spin" />
          </div>
        ) : loadError && entries.length === 0 ? (
          <div className="px-4 py-10 text-center sm:px-6">
            <AlertCircle className="text-danger mx-auto h-8 w-8" />
            <p className="text-fg-muted mt-2 text-sm">Couldn't load time off.</p>
            <button
              onClick={load}
              className="text-accent hover:text-accent mt-2 text-sm font-semibold"
            >
              Try again
            </button>
          </div>
        ) : entries.length === 0 ? (
          <div className="px-4 py-10 text-center sm:px-6">
            <CalendarOff className="text-fg-faint mx-auto h-8 w-8" />
            <p className="text-fg-secondary mt-2 text-sm font-medium">No time off scheduled</p>
            <p className="text-fg-muted mt-1 text-xs">
              Add a block and customers won't be able to book inside it.
            </p>
          </div>
        ) : (
          <ul className="divide-subtle divide-y">
            {entries.map((entry) => (
              <li
                key={entry.id}
                className="flex items-center justify-between gap-3 px-4 py-3.5 sm:px-6"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                      entry.staffId === null
                        ? 'bg-warn-soft text-warn'
                        : 'bg-accent-soft text-accent'
                    }`}
                  >
                    {entry.staffId === null ? (
                      <Store className="h-4 w-4" />
                    ) : (
                      <User className="h-4 w-4" />
                    )}
                  </span>
                  <div className="min-w-0">
                    <p className="text-fg truncate text-sm font-semibold">
                      {entry.staffName ?? 'Whole salon'}
                    </p>
                    <p className="text-fg-muted truncate text-xs">{describe(entry)}</p>
                    {entry.note && (
                      <p className="text-fg-subtle truncate text-xs italic">{entry.note}</p>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    onClick={() => openEdit(entry)}
                    title="Edit"
                    className="text-fg-subtle hover:bg-muted hover:text-fg-secondary rounded-lg p-2 transition-colors"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setDeleteTarget(entry)}
                    title="Delete"
                    className="text-fg-subtle hover:bg-danger-soft hover:text-danger rounded-lg p-2 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <TimeOffDeleteDialog
        open={deleteTarget !== null}
        target={deleteTarget}
        description={deleteTarget ? describe(deleteTarget) : ''}
        onClose={handleDeleteClosed}
      />

      <TimeOffConflictDialog
        open={conflicts.length > 0 && conflictBlockId !== null}
        conflicts={conflicts}
        range={conflictRange}
        timeOffId={conflictBlockId ?? 0}
        onClose={(cancelledAny) => {
          setConflicts([]);
          setConflictBlockId(null);
          if (cancelledAny) onAppointmentsChanged?.();
        }}
      />

      {/* Queued behind the conflict dialog when an edit produced both, so they
          never stack: deal with who you've just cut off, then who you've freed. */}
      <TimeOffRestoreDialog
        open={restorable.length > 0 && conflicts.length === 0}
        restorable={restorable}
        onClose={(restoredAny) => {
          setRestorable([]);
          if (restoredAny) onAppointmentsChanged?.();
        }}
      />
    </div>
  );
}
